import { useEffect, useState } from "react";
import { Inbox, CheckCircle2, XCircle, Edit3, Loader2 } from "lucide-react";
import api from "../../lib/api";

export default function AdminOpsPending() {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState({});  // action_id → edited json string

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/ops/pending");
      setActions(data.actions || []);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, []);

  const decide = async (action, decision, payload) => {
    setBusy(action.action_id);
    try {
      const url = `/admin/ops/pending/${action.action_id}/${decision}`;
      const body = decision === "edit-approve" ? { payload } : {};
      await api.post(url, body);
      await reload();
    } catch (e) {
      alert(e?.response?.data?.detail || "Failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="max-w-4xl">
      <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-zinc-500">
        <Inbox className="h-3 w-3 text-[#00E5FF]" /> Ops
      </div>
      <h1 className="font-display mt-3 text-3xl sm:text-4xl font-semibold tracking-tighter chrome-text">
        Pending actions
      </h1>
      <p className="mt-3 text-[14.5px] text-zinc-400 leading-relaxed max-w-2xl">
        Drafts Nerea has proposed. Approve, edit and approve, or reject.
      </p>

      <div className="mt-7 space-y-3" data-testid="ops-pending-list">
        {loading && <div className="text-zinc-500 text-sm">Loading…</div>}
        {!loading && actions.length === 0 && (
          <div className="rounded-xl border border-dashed border-white/[0.08] p-5 text-[13px] text-zinc-500" data-testid="ops-pending-empty">
            No pending actions.
          </div>
        )}
        {actions.map((a) => {
          const isEditing = editing[a.action_id] !== undefined;
          const draft = isEditing ? editing[a.action_id] : JSON.stringify(a.payload, null, 2);
          return (
            <div key={a.action_id} className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-4" data-testid={`ops-pending-${a.action_id}`}>
              <div className="flex items-center gap-2 text-[10.5px] uppercase tracking-[0.18em]">
                <span className="font-mono px-2 py-0.5 rounded-md bg-white/[0.04] border border-white/[0.08] text-zinc-300">
                  {a.action_type}
                </span>
                <span className="font-mono text-zinc-500">{(a.merchant_id || "").slice(0, 18)}</span>
                <span className="text-zinc-500 ml-auto">{(a.proposed_at || "").slice(0, 19).replace("T", " ")}</span>
              </div>
              <pre
                contentEditable={isEditing}
                suppressContentEditableWarning
                onInput={(e) => setEditing((s) => ({ ...s, [a.action_id]: e.currentTarget.innerText }))}
                className={`mt-3 text-[12px] font-mono text-zinc-200 bg-black/40 rounded-lg p-3 border ${
                  isEditing ? "border-[#00E5FF]/30" : "border-white/[0.05]"
                } whitespace-pre-wrap`}
              >
                {draft}
              </pre>
              <div className="mt-3 flex items-center gap-2">
                <button
                  data-testid={`ops-pending-approve-${a.action_id}`}
                  disabled={busy === a.action_id}
                  onClick={() => decide(a, "approve")}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-emerald-500/10 hover:bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 px-3 py-1.5 text-[12.5px] transition-colors disabled:opacity-40"
                >
                  {busy === a.action_id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
                  Approve
                </button>
                {!isEditing ? (
                  <button
                    data-testid={`ops-pending-edit-${a.action_id}`}
                    onClick={() => setEditing((s) => ({ ...s, [a.action_id]: JSON.stringify(a.payload, null, 2) }))}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-white/[0.08] bg-white/[0.02] hover:bg-white/[0.05] px-3 py-1.5 text-[12.5px] text-zinc-300 transition-colors"
                  >
                    <Edit3 className="h-3.5 w-3.5" /> Edit
                  </button>
                ) : (
                  <button
                    data-testid={`ops-pending-edit-save-${a.action_id}`}
                    disabled={busy === a.action_id}
                    onClick={() => {
                      try {
                        const parsed = JSON.parse(editing[a.action_id]);
                        decide(a, "edit-approve", parsed);
                        setEditing((s) => { const c = { ...s }; delete c[a.action_id]; return c; });
                      } catch {
                        alert("Invalid JSON");
                      }
                    }}
                    className="inline-flex items-center gap-1.5 rounded-lg bg-[#00E5FF]/10 hover:bg-[#00E5FF]/20 border border-[#00E5FF]/30 text-[#00E5FF] px-3 py-1.5 text-[12.5px] transition-colors disabled:opacity-40"
                  >
                    Save & approve
                  </button>
                )}
                <button
                  data-testid={`ops-pending-reject-${a.action_id}`}
                  disabled={busy === a.action_id}
                  onClick={() => decide(a, "reject")}
                  className="inline-flex items-center gap-1.5 rounded-lg bg-rose-500/10 hover:bg-rose-500/20 border border-rose-400/30 text-rose-300 px-3 py-1.5 text-[12.5px] transition-colors disabled:opacity-40 ml-auto"
                >
                  <XCircle className="h-3.5 w-3.5" /> Reject
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
